import { useState, useMemo } from "react";
import { format, parseISO, addDays } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { useDebounceValue } from "usehooks-ts";
import { getEntries } from "@/lib/time-api";
import { getMembers } from "@/lib/members-api";
import { getWeekRange, formatWeekLabel } from "@/lib/time-utils";
import { useOrgId } from "@/lib/org-context";
import { useTimeStore } from "@/stores/time-store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TimesheetMemberRow } from "./TimesheetMemberRow";
import { MemberTimeDialog } from "./MemberTimeDialog";
import { ChevronLeft, ChevronRight, Loader2, Search } from "lucide-react";
import type { TimeEntry, Project } from "@/types/time";

interface OrgMember {
  id: string;
  userId: string;
  role: string;
  user: { name: string; email: string; image: string | null };
}

interface OrgTimesheetGridProps {
  projects: Project[];
  projectColors: Map<string, string>;
  members: OrgMember[];
  currentUserId: string | undefined;
}

interface MemberData {
  id: string;
  userId: string;
  name: string;
  email: string;
  role: string;
  entries: TimeEntry[];
}

export function OrgTimesheetGrid({ projects, projectColors, members, currentUserId }: OrgTimesheetGridProps) {
  const [weekOffset, setWeekOffset] = useState(0);
  const weekRange = getWeekRange(weekOffset);
  const weekLabel = formatWeekLabel(weekRange.start, weekRange.end);

  const orgId = useOrgId();
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounceValue(search, 300);
  const selectedMemberId = useTimeStore((s) => s.selectedMemberId);
  const selectedMemberName = useTimeStore((s) => s.selectedMemberName);
  const selectedMemberEmail = useTimeStore((s) => s.selectedMemberEmail);
  const openMemberDialog = useTimeStore((s) => s.openMemberDialog);
  const closeMemberDialog = useTimeStore((s) => s.closeMemberDialog);
  // Batch fetch all entries
  const { data: entriesData, isLoading: entriesLoading } = useQuery({
    queryKey: ["time-entries", "team", orgId, weekRange.start, weekRange.end],
    queryFn: () =>
      getEntries({
        start_date: weekRange.start,
        end_date: weekRange.end,
        per_page: 200,
      }),
  });

  // Server search (debounced)
  const { data: searchData, isLoading: searchLoading } = useQuery({
    queryKey: ["members", orgId, { search: debouncedSearch.trim() }],
    queryFn: () => getMembers({ search: debouncedSearch.trim() }),
    enabled: debouncedSearch.trim().length > 0,
    staleTime: 10_000,
  });

  // Group entries by member
  const memberEntries = useMemo(() => {
    const map = new Map<string, TimeEntry[]>();
    for (const entry of entriesData?.data ?? []) {
      const existing = map.get(entry.memberId) ?? [];
      existing.push(entry);
      map.set(entry.memberId, existing);
    }
    return map;
  }, [entriesData]);

  // Build member data
  const memberData: MemberData[] = useMemo(() => {
    return members.map((m) => ({
      id: m.id,
      userId: m.userId,
      name: m.user.name ?? "",
      email: m.user.email ?? "",
      role: m.role,
      entries: memberEntries.get(m.id) ?? [],
    }));
  }, [members, memberEntries]);

  const myData = memberData.find((m) => m.userId === currentUserId);
  const othersData = memberData.filter((m) => m.userId !== currentUserId);

  // Use search results if searching
  const displayedOthers = useMemo(() => {
    if (!search.trim() || !searchData) return othersData;
    return searchData.data
      .filter((m) => m.userId !== currentUserId)
      .map((m) => ({
        id: m.id,
        userId: m.userId,
        name: m.user.name,
        email: m.user.email,
        role: m.role,
        entries: memberEntries.get(m.id) ?? [],
      }));
  }, [search, searchData, othersData, currentUserId, memberEntries]);

  // Day headers
  const monday = parseISO(weekRange.start);
  const dayHeaders = Array.from({ length: 7 }, (_, i) => {
    const d = addDays(monday, i);
    return { label: format(d, "EEE"), date: format(d, "MMM d") };
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setWeekOffset((o) => o - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium min-w-[180px] text-center">
            {weekLabel}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setWeekOffset((o) => o + 1)}
            disabled={weekOffset >= 0}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          {weekOffset !== 0 && (
            <Button variant="ghost" size="sm" onClick={() => setWeekOffset(0)}>
              This week
            </Button>
          )}
        </div>
        <div className="relative w-[220px]">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search member..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
          {searchLoading && (
            <Loader2 className="absolute right-2.5 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />
          )}
        </div>
      </div>

      {entriesLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-6">
          <div
            className="grid items-end gap-2 px-2"
            style={{ gridTemplateColumns: "180px repeat(7, 1fr) 60px" }}
          >
            <div />
            {dayHeaders.map((h, i) => (
              <div key={i} className="text-center">
                <p className="text-xs font-medium">{h.label}</p>
                <p className="text-[10px] text-muted-foreground">{h.date}</p>
              </div>
            ))}
            <div className="text-right text-xs font-medium">Total</div>
          </div>

          {myData && !search.trim() && (
            <div className="mb-2">
              <p className="mb-1 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                My Time
              </p>
              <TimesheetMemberRow
                name={myData.name}
                email={myData.email}
                entries={myData.entries}
                weekStart={weekRange.start}
                projectColors={projectColors}

                onClick={() => openMemberDialog(myData.id, myData.name, myData.email)}
              />
            </div>
          )}

          {!search.trim() && <div className="border-t my-4" />}

          <div>
            <p className="mb-1 text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {search.trim() ? `Results (${displayedOthers.length})` : "Others"}
            </p>
            {displayedOthers.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                {search.trim() ? "No members match your search" : "No other members"}
              </p>
            ) : (
              displayedOthers.map((member) => (
                <TimesheetMemberRow
                  key={member.id}
                  name={member.name}
                  email={member.email}
                  entries={member.entries}
                  weekStart={weekRange.start}
                  projectColors={projectColors}
  
                  onClick={() => openMemberDialog(member.id, member.name, member.email)}
                />
              ))
            )}
          </div>
        </div>
      )}

      <MemberTimeDialog
        open={!!selectedMemberId}
        onOpenChange={(open) => !open && closeMemberDialog()}
        memberId={selectedMemberId ?? ""}
        name={selectedMemberName}
        email={selectedMemberEmail}
        projects={projects}
        projectColors={projectColors}
      />
    </div>
  );
}
